"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IdentityAuthStatus = exports.IdentityLocation = exports.SecureNetworkEventType = exports.SecureNetworkAgent = void 0;
const NetworkAgent_1 = require("../network/NetworkAgent");
const random_1 = require("../../../crypto/random");
const hmac_1 = require("../../../crypto/hmac");
const ciphers_1 = require("../../../crypto/ciphers");
const identity_1 = require("../../../data/identity");
const model_1 = require("../../../data/model");
const strings_1 = require("../../../util/strings");
const logging_1 = require("../../../util/logging");
var IdHolderStatus;
(function (IdHolderStatus) {
    IdHolderStatus["ExpectingChallenge"] = "expecting-challenge";
    IdHolderStatus["ReceivedUnexpectedChallenge"] = "received-unexpected-challenge";
    IdHolderStatus["ReceivedUnexpectedIdentityRequest"] = "received-unexpected-identity-request";
    IdHolderStatus["SentIdentity"] = "sent-identity";
    IdHolderStatus["SentChallengeAnswer"] = "sent-challenge-answer";
    IdHolderStatus["IdentityVerified"] = "identity-verified";
    IdHolderStatus["IdentityRejected"] = "identity-rejected";
})(IdHolderStatus || (IdHolderStatus = {}));
;
var IdChallengerStatus;
(function (IdChallengerStatus) {
    IdChallengerStatus["WaitingToChallenge"] = "waiting-to-challenge";
    IdChallengerStatus["SentIdentityRequest"] = "sent-identity-request";
    IdChallengerStatus["SentChallenge"] = "sent-challenge";
    IdChallengerStatus["IdentityVerified"] = "identity-verified";
    IdChallengerStatus["IdentityRejected"] = "identity-rejected";
})(IdChallengerStatus || (IdChallengerStatus = {}));
// The following state machine models conneciton validation:
// id holder:
// nil -> 'received-unexpected-identity-request'     on 'request-identity' message reception
// nil -> 'received-unexpected-challenge'            on 'send-challenge'   message reception
// nil -> 'expecting-challenge'                      on answerIdentityChallenge() call
// 'expecting-challenge' -> 'sent-identity'          on 'request-identity' message reception
// 'sent-identity'       -> 'sent-challenge-answer'  on 'send-challenge'   message reception
// 'expecting-challenge' -> 'sent-challenge-answer'  on 'send-challenge'   message reception
// 'sent-challenge-answer' -> 'identity-verified'    on 'challenge-result' message reception (accepted)
// 'sent-challenge-answer' -> 'identity-rejected'    on 'challenge-result' message reception (rejected)
// id challenger:
// nil -> 'waiting-to-challenge'      on sendIdentityChallenge() call (connection not ready yet)
// nil -> 'sent-identity-request'     on sendIdentityChallenge() call, without identity (connected)
// nil -> 'sent-challenge'            on sendIdentityChallenge() call, with identity    (connected)
// 'waiting-to-challenge'  -> 'sent-identity-request' on connection ready, no identity
// 'waiting-to-challenge'  -> 'sent-challenge'        on connection ready, identity present
// 'sent-identity-request' -> 'sent-challenge'     on 'send-identity' message reception
// 'sent-challente'        -> 'identity-verified'  on 'answer-challenge' message reception (accepted)
// 'sent-challenge'        -> 'identity-rejected'  on 'answer-challenge'  message reception (rejected)
var MessageType;
(function (MessageType) {
    MessageType["RequestIdentity"] = "request-identity";
    MessageType["SendIdentity"] = "send-identity";
    MessageType["SendChallenge"] = "send-challenge";
    MessageType["AnswerChallenge"] = "answer-challenge";
    MessageType["ChallengeResult"] = "challenge-result";
    MessageType["SecureMessage"] = "secure-message";
})(MessageType || (MessageType = {}));
;
var SecureNetworkEventType;
(function (SecureNetworkEventType) {
    SecureNetworkEventType["SecureMessageReceived"] = "secure-message-received";
    SecureNetworkEventType["ConnectionIdentityAuth"] = "connection-identity-auth";
})(SecureNetworkEventType || (SecureNetworkEventType = {}));
exports.SecureNetworkEventType = SecureNetworkEventType;
var IdentityAuthStatus;
(function (IdentityAuthStatus) {
    IdentityAuthStatus["Accepted"] = "accepted";
    IdentityAuthStatus["Rejected"] = "rejected";
    IdentityAuthStatus["Requested"] = "requested";
})(IdentityAuthStatus || (IdentityAuthStatus = {}));
exports.IdentityAuthStatus = IdentityAuthStatus;
;
var IdentityLocation;
(function (IdentityLocation) {
    IdentityLocation["Local"] = "local";
    IdentityLocation["Remote"] = "remote";
})(IdentityLocation || (IdentityLocation = {}));
exports.IdentityLocation = IdentityLocation;
class OneWaySecuredConnection {
    constructor(connectionId, identityHash) {
        this.connId = connectionId;
        this.identityHash = identityHash;
    }
    verified() {
        return this.status === IdHolderStatus.IdentityVerified;
    }
    generateSecret() {
        this.secret = new random_1.RNGImpl().randomHexString(256);
        // use 256 bits so it can be use as a ChaCha20 key
    }
    async encryptSecret() {
        if (this.secret === undefined) {
            throw new Error('Attempted to encrypt connection secret before generating it.');
        }
        if (this.identity === undefined) {
            throw new Error('Attempted to encrypt connection secret, but identity is missing.');
        }
        const encryptedSecret = new Array();
        for (const chunk of strings_1.Strings.chunk(this.secret, 32)) {
            encryptedSecret.push(await this.identity.encrypt(chunk));
        }
        this.encryptedSecret = encryptedSecret;
    }
    async decryptSecret() {
        if (this.identity === undefined) {
            throw new Error('Secured connection cannot decrypt received secret: identity is missing');
        }
        if (!this.identity.hasKeyPair()) {
            throw new Error('Secured connection cannot decrypt received secret: using an identity without a key pair');
        }
        if (this.encryptedSecret === undefined) {
            throw new Error('Secured connection cannot decrypt received secret: it is missing');
        }
        const chunks = new Array();
        for (const encryptedChunk of this.encryptedSecret) {
            chunks.push(await this.identity.decrypt(encryptedChunk));
        }
        this.secret = strings_1.Strings.unchunk(chunks);
    }
    computeSecretHash() {
        if (this.secret === undefined) {
            throw new Error('Cannot hash secret: it is missing');
        }
        return model_1.Hashing.forString(this.secret);
    }
    setTimeout(seconds) {
        this.timeout = new Date().getTime() + seconds * 1000;
    }
    encode() {
        return OneWaySecuredConnection.encode(this.connId, this.identityHash);
    }
    static encode(connectionId, identity) {
        return connectionId + OneWaySecuredConnection.Separator + identity;
    }
    static decode(encoded) {
        const parts = encoded.split(OneWaySecuredConnection.Separator);
        return new OneWaySecuredConnection(parts[0], parts[1]);
    }
}
OneWaySecuredConnection.Separator = '__';
class ConnectionSecuredForReceiving extends OneWaySecuredConnection {
    constructor(connectionId, identityHash) {
        super(connectionId, identityHash);
    }
}
class ConnectionSecuredForSending extends OneWaySecuredConnection {
    constructor(connectionId, identityHash) {
        super(connectionId, identityHash);
    }
}
const DEFAULT_TIMEOUT = 15;
class SecureNetworkAgent {
    constructor() {
        this.remoteIdentities = new Map();
        this.localIdentities = new Map();
    }
    getAgentId() {
        return SecureNetworkAgent.Id;
    }
    ready(pod) {
        this.pod = pod;
    }
    receiveLocalEvent(ev) {
        if (ev.type === NetworkAgent_1.NetworkEventType.ConnectionStatusChange) {
            let connEv = ev;
            if (connEv.content.status === NetworkAgent_1.ConnectionStatus.Closed) {
                this.removeIdentitiesForConnection(ev.content.connId);
            }
            else if (connEv.content.status === NetworkAgent_1.ConnectionStatus.Ready) {
                for (const secured of this.remoteIdentities.values()) {
                    if (secured.connId === connEv.content.localEndpoint &&
                        secured.status === IdChallengerStatus.WaitingToChallenge) {
                        this.sendChallengeMessage(secured);
                    }
                }
            }
        }
        else if (ev.type === NetworkAgent_1.NetworkEventType.MessageReceived) {
            let msgEv = ev;
            this.receiveMessage(msgEv.content.connectionId, msgEv.content.source, msgEv.content.destination, msgEv.content.content);
        }
    }
    // for identity holder:
    secureForReceiving(connId, localIdentity, timeout = DEFAULT_TIMEOUT) {
        SecureNetworkAgent.logger.trace(() => 'Asked to verify ' + connId + ' for receiving with ' + localIdentity.hash());
        const identityHash = localIdentity.hash();
        let secured = this.getOrCreateConnectionSecuredForReceiving(connId, identityHash);
        secured.identity = localIdentity;
        secured.setTimeout(timeout);
        if (secured.status === IdHolderStatus.ReceivedUnexpectedIdentityRequest) {
            this.sendIdentity(connId, localIdentity, identityHash);
            secured.status = IdHolderStatus.SentIdentity;
        }
        else if (secured.status === IdHolderStatus.ReceivedUnexpectedChallenge) {
            secured.decryptSecret().then(() => {
                //TODO: see if we have introduced a race condition by making decryptSecret async.
                this.answerReceivedChallenge(connId, identityHash, secured.computeSecretHash());
                secured.status = IdHolderStatus.SentChallengeAnswer;
            });
        }
        else if (secured.status === undefined) {
            secured.status = IdHolderStatus.ExpectingChallenge;
        } // else, negotiation is already running, just let it run its course
    }
    // for identity challenger:
    secureForSending(connId, remoteIdentityHash, remoteIdentity, timeout = DEFAULT_TIMEOUT) {
        let connInfo = this.getNetworkAgent().getConnectionInfo(connId);
        if ((connInfo === null || connInfo === void 0 ? void 0 : connInfo.status) !== NetworkAgent_1.ConnectionStatus.Closed) {
            let secured = this.getOrCreateConnectionSecuredForSending(connId, remoteIdentityHash);
            secured.setTimeout(timeout);
            if (secured.identity === undefined) {
                secured.identity = remoteIdentity;
            }
            if ((connInfo === null || connInfo === void 0 ? void 0 : connInfo.status) === NetworkAgent_1.ConnectionStatus.Ready) {
                if (secured.status === undefined || secured.status === 'identity-rejected') {
                    this.sendChallengeMessage(secured);
                } // else, negotiation is already running, just let it run its course
            }
            else if ((connInfo === null || connInfo === void 0 ? void 0 : connInfo.status) === NetworkAgent_1.ConnectionStatus.Received ||
                (connInfo === null || connInfo === void 0 ? void 0 : connInfo.status) === NetworkAgent_1.ConnectionStatus.Establishing) {
                secured.status = IdChallengerStatus.WaitingToChallenge;
            }
        }
    }
    sendChallengeMessage(secured) {
        if (secured.identity === undefined) {
            this.sendIdentityRequest(secured.connId, secured.identityHash);
            secured.status = IdChallengerStatus.SentIdentityRequest;
        }
        else {
            secured.generateSecret();
            //TODO: see if we have introduced a race condition by making encryptSecret async
            secured.encryptSecret().then(() => {
                this.sendKnownIdentityChallenge(secured.connId, secured.identityHash, secured.encryptedSecret);
                secured.status = IdChallengerStatus.SentChallenge;
            });
        }
    }
    // query for already verified local or remote identities
    getLocalVerifiedIdentity(connId, identityHash) {
        return this.getVerifiedIdentity(connId, identityHash, true);
    }
    getRemoteVerifiedIdentity(connId, identityHash) {
        return this.getVerifiedIdentity(connId, identityHash, false);
    }
    // messaging, usable once both supplied identities (sender & recipient) 
    // have been verified on that connection
    sendSecurely(connId, sender, recipient, agentId, content) {
        let remote = this.getConnectionSecuredForSending(connId, recipient);
        let local = this.getConnectionSecuredForReceiving(connId, sender);
        if ((remote === null || remote === void 0 ? void 0 : remote.verified()) && (local === null || local === void 0 ? void 0 : local.verified())) {
            let secureMessagePayload = {
                senderIdentityHash: sender,
                agentId: agentId,
                content: content
            };
            let nonce = new random_1.RNGImpl().randomHexString(96);
            let plaintext = JSON.stringify(secureMessagePayload);
            let payload = new ciphers_1.ChaCha20Impl().encryptHex(plaintext, remote.secret, nonce);
            let hmac = new hmac_1.HMACImpl().hmacSHA256hex(payload, local.secret);
            let secureMessage = {
                type: MessageType.SecureMessage,
                identityHash: recipient,
                nonce: nonce,
                payload: payload,
                hmac: hmac
            };
            this.getNetworkAgent().sendMessage(connId, SecureNetworkAgent.Id, secureMessage);
        }
        else {
            throw new Error('Connection ' + connId + ' still has not verified both sender ' + sender + ' and recipient ' + recipient + '.');
        }
    }
    // incoming message processing
    receiveMessage(connId, source, destination, content) {
        var _a;
        source;
        destination;
        let controlMessage = content;
        let identityHash = controlMessage.identityHash;
        SecureNetworkAgent.logger.trace(() => 'Received message ' + JSON.stringify(content));
        // for id holder:
        if (controlMessage.type === MessageType.RequestIdentity) {
            let secured = this.getOrCreateConnectionSecuredForReceiving(connId, identityHash);
            if (secured.status === IdHolderStatus.ExpectingChallenge) {
                this.sendIdentity(connId, secured.identity, identityHash);
                secured.status = IdHolderStatus.SentIdentity;
            }
            else if (secured.status === undefined) {
                secured.status = IdHolderStatus.ReceivedUnexpectedIdentityRequest;
                this.sendAuthEvent(connId, IdentityLocation.Local, identityHash, IdentityAuthStatus.Requested, secured.identity);
            }
        }
        else if (controlMessage.type === MessageType.SendChallenge) {
            let sendChallengeMessage = content;
            let secured = this.getOrCreateConnectionSecuredForReceiving(connId, identityHash);
            if (secured.status === IdHolderStatus.ExpectingChallenge ||
                secured.status === IdHolderStatus.SentIdentity) {
                secured.encryptedSecret = sendChallengeMessage.encrypedSecret;
                secured.decryptSecret().then(() => {
                    //TODO: see if we have introduced a race condition by making decryptSecret async.
                    this.answerReceivedChallenge(connId, identityHash, secured.computeSecretHash());
                    secured.status = IdHolderStatus.SentChallengeAnswer;
                });
            }
            else if (secured.status === undefined) {
                secured.encryptedSecret = sendChallengeMessage.encrypedSecret;
                secured.status = IdHolderStatus.ReceivedUnexpectedChallenge;
                this.sendAuthEvent(connId, IdentityLocation.Local, identityHash, IdentityAuthStatus.Requested, secured.identity);
            }
        }
        else if (controlMessage.type === MessageType.ChallengeResult) {
            let challengeResultMessage = content;
            let secured = this.getOrCreateConnectionSecuredForReceiving(connId, identityHash);
            if (secured.status === IdHolderStatus.SentChallengeAnswer) {
                if (challengeResultMessage.result) {
                    secured.status = IdHolderStatus.IdentityVerified;
                }
                else {
                    secured.status = IdHolderStatus.IdentityRejected;
                }
                const authStatus = challengeResultMessage.result ? IdentityAuthStatus.Accepted : IdentityAuthStatus.Rejected;
                this.sendAuthEvent(connId, IdentityLocation.Local, secured.identityHash, authStatus, secured.identity);
            }
        }
        // for id challenger:
        else if (controlMessage.type === MessageType.SendIdentity) {
            let sendIdentityMessage = content;
            let secured = this.getOrCreateConnectionSecuredForSending(connId, identityHash);
            if (secured.status === IdChallengerStatus.SentIdentityRequest) {
                let identity = model_1.HashedObject.fromLiteralContext(sendIdentityMessage.identity);
                if (identity.hash() === identityHash && identity instanceof identity_1.Identity) {
                    secured.identity = identity;
                    secured.generateSecret();
                    //TODO: see if we have introduced a race condition by making encryptSecret async
                    secured.encryptSecret().then(() => {
                        this.sendKnownIdentityChallenge(connId, identityHash, secured.encryptedSecret);
                        secured.status = IdChallengerStatus.SentChallenge;
                    });
                }
                else {
                    secured.status = IdChallengerStatus.IdentityRejected;
                }
            }
        }
        else if (controlMessage.type === MessageType.AnswerChallenge) {
            let answerChallengeMessage = content;
            let secured = this.getOrCreateConnectionSecuredForSending(connId, identityHash);
            if (secured.status === IdChallengerStatus.SentChallenge) {
                let accepted = answerChallengeMessage.secretHash === secured.computeSecretHash();
                if (accepted) {
                    this.sendChallengeResult(connId, identityHash, true);
                    secured.status = IdChallengerStatus.IdentityVerified;
                }
                else {
                    this.sendChallengeResult(connId, identityHash, false);
                    secured.status = IdChallengerStatus.IdentityRejected;
                }
                const authStatus = accepted ? IdentityAuthStatus.Accepted : IdentityAuthStatus.Rejected;
                this.sendAuthEvent(connId, IdentityLocation.Remote, secured.identityHash, authStatus, secured.identity);
            }
        }
        // for both:
        else if (controlMessage.type === MessageType.SecureMessage) {
            let secureMessage = content;
            let local = this.getConnectionSecuredForReceiving(connId, secureMessage.identityHash);
            if (local === null || local === void 0 ? void 0 : local.verified()) {
                let payload = new ciphers_1.ChaCha20Impl().decryptHex(secureMessage.payload, local.secret, secureMessage.nonce);
                let secureMessagePayload = JSON.parse(payload);
                let remote = this.getConnectionSecuredForSending(connId, secureMessagePayload.senderIdentityHash);
                if (remote === null || remote === void 0 ? void 0 : remote.verified()) {
                    let hmac = new hmac_1.HMACImpl().hmacSHA256hex(secureMessage.payload, remote.secret);
                    if (secureMessage.hmac === hmac) {
                        let agent = (_a = this.pod) === null || _a === void 0 ? void 0 : _a.getAgent(secureMessagePayload.agentId);
                        if (agent !== undefined) {
                            let event = {
                                type: SecureNetworkEventType.SecureMessageReceived,
                                content: {
                                    connId: connId,
                                    sender: secureMessagePayload.senderIdentityHash,
                                    recipient: secureMessage.identityHash,
                                    payload: secureMessagePayload.content
                                }
                            };
                            agent.receiveLocalEvent(event);
                        }
                    }
                    else {
                        SecureNetworkAgent.logger.warning('HMAC mismatch on received message on connection ' + connId);
                    }
                }
            }
        }
        ;
    }
    shutdown() {
    }
    sendAuthEvent(connId, identityLocation, identityHash, status, identity) {
        var _a;
        let ev = {
            type: SecureNetworkEventType.ConnectionIdentityAuth,
            content: {
                connId: connId,
                identityLocation: identityLocation,
                identityHash: identityHash,
                identity: identity,
                status: status
            }
        };
        (_a = this.pod) === null || _a === void 0 ? void 0 : _a.broadcastEvent(ev);
    }
    sendIdentity(connId, identity, identityHash) {
        if (identityHash === undefined) {
            identityHash = identity.hash();
        }
        let content = {
            type: MessageType.SendIdentity,
            identityHash: identityHash,
            identity: identity.toLiteralContext()
        };
        SecureNetworkAgent.logger.trace('Sending id ' + identityHash + ' on ' + connId);
        this.sendControlMessage(connId, content);
    }
    answerReceivedChallenge(connId, identityHash, secretHash) {
        let content = {
            type: MessageType.AnswerChallenge,
            identityHash: identityHash,
            secretHash: secretHash
        };
        this.sendControlMessage(connId, content);
    }
    sendIdentityRequest(connId, identityHash) {
        let content = {
            type: MessageType.RequestIdentity,
            identityHash: identityHash
        };
        this.sendControlMessage(connId, content);
    }
    sendKnownIdentityChallenge(connId, identityHash, encryptedSecret) {
        let content = {
            type: MessageType.SendChallenge,
            identityHash: identityHash,
            encrypedSecret: encryptedSecret
        };
        this.sendControlMessage(connId, content);
    }
    sendChallengeResult(connId, identityHash, verified) {
        let content = {
            type: MessageType.ChallengeResult,
            identityHash: identityHash,
            result: verified
        };
        this.sendControlMessage(connId, content);
    }
    sendControlMessage(connId, content) {
        this.getNetworkAgent().sendMessage(connId, SecureNetworkAgent.Id, content);
    }
    getOrCreateConnectionSecuredForSending(connId, identityHash) {
        return this.getOneWaySecuredConnection(connId, identityHash, false, true);
    }
    getOrCreateConnectionSecuredForReceiving(connId, identityHash) {
        return this.getOneWaySecuredConnection(connId, identityHash, true, true);
    }
    getConnectionSecuredForSending(connId, identityHash) {
        return this.getOneWaySecuredConnection(connId, identityHash, false, false);
    }
    getConnectionSecuredForReceiving(connId, identityHash) {
        return this.getOneWaySecuredConnection(connId, identityHash, true, false);
    }
    getOneWaySecuredConnection(connId, identityHash, sender, create) {
        let key = OneWaySecuredConnection.encode(connId, identityHash);
        let map;
        if (sender) {
            map = this.localIdentities;
        }
        else {
            map = this.remoteIdentities;
        }
        let secured = map.get(key);
        if (create && secured === undefined) {
            if (sender) {
                secured = new ConnectionSecuredForReceiving(connId, identityHash);
            }
            else {
                secured = new ConnectionSecuredForSending(connId, identityHash);
            }
            secured.setTimeout(DEFAULT_TIMEOUT);
            map.set(key, secured);
        }
        return secured;
    }
    getVerifiedIdentity(connId, identityHash, local) {
        let sid = this.getOneWaySecuredConnection(connId, identityHash, local, false);
        let identity = undefined;
        if (sid !== undefined && sid.verified()) {
            identity = sid.identity;
        }
        return identity;
    }
    removeIdentitiesForConnection(id) {
        var _a;
        for (const verifiedIdentities of [this.remoteIdentities, this.localIdentities]) {
            const toRemove = new Array();
            for (const [k, v] of verifiedIdentities.entries()) {
                if (v.connId === id) {
                    toRemove.push(k);
                }
            }
            for (const k of toRemove) {
                SecureNetworkAgent.logger.trace('Removing identity' + ((_a = verifiedIdentities.get(k)) === null || _a === void 0 ? void 0 : _a.identityHash) + ' from connection ' + id + ': it is being closed.');
                verifiedIdentities.delete(k);
            }
        }
    }
    getNetworkAgent() {
        return this.pod.getAgent(NetworkAgent_1.NetworkAgent.AgentId);
    }
}
exports.SecureNetworkAgent = SecureNetworkAgent;
SecureNetworkAgent.logger = new logging_1.Logger(SecureNetworkAgent.name, logging_1.LogLevel.INFO);
SecureNetworkAgent.Id = 'secure-connection-agent';
//# sourceMappingURL=SecureNetworkAgent.js.map